import { useMemo, useState, useEffect, useCallback, useRef } from "react";

import { type Collection } from "@shopify/hydrogen-react/storefront-api-types";
import useSWR from "swr/immutable";

import getCollectionByHandleQuery from "@graphql/get-collection-by-handle.graphql";
import { storefrontAPIFetcher } from "@react/hooks/fetchers";
import { useBreakpoint } from "@react/hooks/useBreakpoint";
import { COLLECTION_FILTER_IDS } from "@react/sections/Collection/CollectionFilters/constants";
import { useCollectionFilters } from "@react/sections/Collection/CollectionFiltersContext";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import {
  filterProductByVariantEligibility,
  formatCollectionContentCards,
  formatAdjacentCollections,
  getCollectionGridProductDeficit,
  getCollectionGridProductPositions,
  shouldShowCollectionContentCards,
} from "@react/utilities/collection";
import { STOREFRONT_API_URL } from "@tablescape/constants";
import { isCollectionFilterId } from "@tablescape/react/utilities/collectionFilters";
import {
  type CollectionSectionSettings,
  type CollectionConnection,
  type OPProductConnection,
} from "@tablescape/state";

export const useCollectionQueryParams = [
  STOREFRONT_API_URL,
  getCollectionByHandleQuery,
];

type CollectionPageRequest = {
  cursor: string;
  scopeKey: string;
  size: number;
};

export function useCollectionQuery(
  collectionHandle: Collection["handle"] | undefined
) {
  const {
    meta: {
      localization: { country, language },
    },
    pagination: { pageSize: defaultPageSize },
  } = useSectionSettings<CollectionSectionSettings>();

  /** Merchant/configured products-per-page for initial load and user Load More. */
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Strict `<` matches Tailwind `min-width` utilities for grid track math.
  const isBelowMd = useBreakpoint("md", { comparison: "lt" });
  const isBelowMg = useBreakpoint("mg", { comparison: "lt" });

  /**
   * Visible count at the last silent top-up attempt. Stall = fetch finished
   * without growing visible products (stops no-progress loops).
   */
  const lastTopUpVisibleCountRef = useRef<number | null>(null);
  const topUpScopeRef = useRef(
    `${collectionHandle ?? ""}:${isBelowMd}:${isBelowMg}`
  );

  const {
    appliedFilters,
    appliedSort,
    availableSorts,
    setAvailableFilters,
    setCollectionPriceMax,
  } = useCollectionFilters();

  const sortValue = useMemo(
    () => appliedSort?.value ?? undefined,
    [appliedSort?.value]
  );

  const filterInputs = useMemo(() => {
    return appliedFilters.map((f) => JSON.parse(String(f.input)));
  }, [appliedFilters]);

  const stateKey = useMemo(
    () => JSON.stringify({ filterInputs, sortValue }),
    [filterInputs, sortValue]
  );

  const scopeKey = `${collectionHandle ?? ""}|${stateKey}`;

  /** Next-page request, pinned to the filters/sort/handle scope that issued it. */
  const [pageRequest, setPageRequest] = useState<CollectionPageRequest | null>(
    null
  );

  // Render-phase adjustment: a pageRequest from a previous filters/sort/handle
  // scope must not survive a round trip back (A→B→A would resume from a stale
  // cursor and append onto the wrong product list). Cleared during render so
  // no commit ever pairs a stale cursor with settled data.
  if (pageRequest !== null && pageRequest.scopeKey !== scopeKey) {
    setPageRequest(null);
  }

  const activeRequest =
    pageRequest !== null && pageRequest.scopeKey === scopeKey
      ? pageRequest
      : null;
  const cursor = activeRequest?.cursor ?? null;
  /** `first` for the current Storefront request (may be a smaller top-up). */
  const fetchSize = activeRequest?.size ?? pageSize;

  const {
    data,
    isLoading: isFetching,
    isValidating,
  } = useSWR<CollectionConnection>(
    collectionHandle && country
      ? [
          ...useCollectionQueryParams,
          {
            handle: collectionHandle,
            country,
            language,
            filters: filterInputs,
            pageSize: fetchSize,
            ...(cursor && { cursor }),
            ...(sortValue && {
              sortKey: sortValue.sortKey,
              reverse: sortValue.reverse,
            }),
          },
        ]
      : null,
    storefrontAPIFetcher,
    // Load-bearing: with keepPreviousData (global SWRConfig), a key change can
    // expose the previous key's page with isValidating=false, and the fold
    // below would append it as if it were the next page. Disabling it
    // guarantees `data` always belongs to the current key.
    { keepPreviousData: false }
  );
  const collection = data?.data?.collection;

  // keepPreviousData is off (see the useSWR options), so `data` is undefined
  // during every fetch. Keep the last settled payload so the title, hero,
  // cards, and nav don't unmount mid-fetch. Pagination (products, hasNextPage,
  // endCursor) must still read the live payload only — never start a request
  // from held data.
  const [settledCollection, setSettledCollection] = useState(collection);
  if (collection !== undefined && collection !== settledCollection) {
    setSettledCollection(collection);
  }
  const displayCollection = collection ?? settledCollection;

  const products = collection?.products;
  const hasNextPage = products?.pageInfo.hasNextPage;
  const endCursor = products?.pageInfo.endCursor;
  const fetchedPageSize = collection?.pageSize?.value;
  const pageOnScroll = displayCollection?.pageOnScroll?.value;
  const filteredProducts = useMemo(() => {
    return products?.edges.map((edge) => edge.node) ?? [];
  }, [products?.edges]);

  const [loaded, setLoaded] = useState<{
    products: OPProductConnection[];
    replaced: boolean;
    requestId: string | null;
  }>({ products: [], replaced: false, requestId: null });

  const requestId =
    collectionHandle && country
      ? JSON.stringify([scopeKey, cursor, fetchSize])
      : null;

  // Fold each settled page exactly once. Settled = SWR is idle and `data`
  // belongs to the current key (keepPreviousData: false guarantees that).
  // The functional update is idempotent under double-invoked effects: a
  // second queued updater sees the first's result and bails on requestId.
  useEffect(() => {
    if (
      requestId === null ||
      isFetching ||
      isValidating ||
      data === undefined
    ) {
      return;
    }
    setLoaded((prev) =>
      prev.requestId === requestId
        ? prev
        : {
            products:
              cursor === null
                ? filteredProducts
                : [...prev.products, ...filteredProducts],
            replaced: cursor === null,
            requestId,
          }
    );
  }, [cursor, data, filteredProducts, isFetching, isValidating, requestId]);

  const contentCards = useMemo(
    () =>
      displayCollection?.contentCards?.references.nodes
        ? formatCollectionContentCards(
            displayCollection.contentCards.references.nodes
          )
        : null,
    [displayCollection?.contentCards?.references.nodes]
  );

  // Process variant match filter similar to Liquid template
  const variantMatchFilter = useMemo(() => {
    const rawFilter = displayCollection?.variantMatchFilter?.value ?? "";
    if (!rawFilter) return "";

    // Extract the value after '=' and clean it up
    const parts = rawFilter.split("=");
    const value = parts[parts.length - 1]?.trim() || "";
    return value.replace(/\+/g, " ");
  }, [displayCollection?.variantMatchFilter?.value]);

  const showContentCards = useMemo(
    () =>
      shouldShowCollectionContentCards({
        contentCards,
        appliedFiltersCount: appliedFilters.length,
        appliedSortName: appliedSort?.name,
        defaultSortName: availableSorts[0]?.name,
      }),
    [contentCards, appliedFilters.length, appliedSort?.name, availableSorts]
  );

  const variantEligibleProductResults = useMemo(
    () =>
      loaded.products.filter((product) =>
        filterProductByVariantEligibility(
          product,
          variantMatchFilter,
          appliedFilters
        )
      ),
    [loaded.products, variantMatchFilter, appliedFilters]
  );

  const visibleProductCount = variantEligibleProductResults.length;

  const contentCardPositions = useMemo(
    () => contentCards?.map((card) => card.position) ?? [],
    [contentCards]
  );

  const productPositions = useMemo(
    () =>
      getCollectionGridProductPositions({
        productCount: variantEligibleProductResults.length,
        contentCardPositions,
        showContentCards,
        columns: isBelowMg ? 2 : 3,
        contentCardTrackSpan: isBelowMd ? 2 : 1,
        dense: isBelowMd,
      }),
    [
      contentCardPositions,
      isBelowMd,
      isBelowMg,
      showContentCards,
      variantEligibleProductResults.length,
    ]
  );

  const productDeficit = getCollectionGridProductDeficit({
    showContentCards,
    productCount: visibleProductCount,
    contentCardPositions,
    isBelowMd,
    isBelowMg,
  });

  // Derived, not stored: a next page was requested, its page has not folded
  // yet, and it can still arrive (in flight, validating, or settled awaiting
  // fold). Cache-hit pages settle immediately, so the flag cannot wedge.
  const isLoadingMore =
    cursor !== null &&
    loaded.requestId !== requestId &&
    (isFetching || isValidating || data !== undefined);

  // Stall only after a silent top-up finishes with no visible growth — not
  // while the request is in flight (so Load More stays hidden during top-up).
  const topUpStalled =
    lastTopUpVisibleCountRef.current !== null &&
    !isLoadingMore &&
    visibleProductCount <= lastTopUpVisibleCountRef.current;

  const pendingTopUp =
    productDeficit > 0 &&
    !!hasNextPage &&
    !!endCursor &&
    endCursor !== cursor &&
    !topUpStalled;

  useEffect(() => {
    if (products?.filters) {
      // Display only whitelisted filters (internal-only filters are excluded)
      const filtered = products.filters.filter((f) =>
        isCollectionFilterId(f.id)
      );
      setAvailableFilters(filtered);
    }
  }, [products?.filters, setAvailableFilters]);

  useEffect(() => {
    const priceFilter = products?.filters?.find(
      (filter) => filter.id === COLLECTION_FILTER_IDS.PRICE
    );

    if (priceFilter?.values?.[0]?.input) {
      const parsed = JSON.parse(String(priceFilter.values[0].input));
      const max = parsed?.price?.max;
      if (typeof max === "number") {
        setCollectionPriceMax(max);
      }
    }
  }, [products?.filters, setCollectionPriceMax, collectionHandle]);

  // Sync the merchant-configured page size; mid-cursor top-up sizes stay
  // stable because they are frozen into the active pageRequest.
  useEffect(() => {
    if (fetchedPageSize) {
      setPageSize(parseInt(fetchedPageSize, 10));
    }
  }, [fetchedPageSize]);

  // New scope: forget the stall baseline. pageRequest is cleared during
  // render (see above); products replace themselves via the cursor-null fold.
  useEffect(() => {
    lastTopUpVisibleCountRef.current = null;
  }, [stateKey, collectionHandle]);

  // State (not derived) on purpose: the initial `true` paints the placeholder
  // before SWR reports, and each isFetching transition produces a committed
  // render (the jest SWR automock only advances per render).
  useEffect(() => {
    setIsLoading(isFetching);
  }, [isFetching]);

  /**
   * Fetch the next page. Silent top-up (`userInitiated: false`) uses the grid
   * deficit; user Load More uses the configured page size. No imperative
   * loading flag: isLoadingMore derives from pageRequest vs the last fold.
   */
  const loadMore = useCallback(
    (userInitiated = true) => {
      if (!hasNextPage || !endCursor || endCursor === cursor) {
        return;
      }

      const isTopUp = !userInitiated && productDeficit > 0;
      if (isTopUp) {
        lastTopUpVisibleCountRef.current = visibleProductCount;
      }

      setPageRequest({
        cursor: endCursor,
        scopeKey,
        size: isTopUp ? productDeficit : pageSize,
      });
    },
    [
      cursor,
      endCursor,
      hasNextPage,
      pageSize,
      productDeficit,
      scopeKey,
      visibleProductCount,
    ]
  );

  // After products settle: if content cards leave a track deficit, silently
  // top up once via loadMore (fetch size decided inside loadMore).
  useEffect(() => {
    const scopeKey = `${collectionHandle ?? ""}:${isBelowMd}:${isBelowMg}`;
    if (topUpScopeRef.current !== scopeKey) {
      lastTopUpVisibleCountRef.current = null;
      topUpScopeRef.current = scopeKey;
    }

    if (
      isFetching ||
      isLoadingMore ||
      !hasNextPage ||
      requestId === null ||
      loaded.requestId !== requestId
    ) {
      return;
    }

    if (productDeficit === 0) {
      lastTopUpVisibleCountRef.current = null;
      return;
    }

    if (
      lastTopUpVisibleCountRef.current !== null &&
      visibleProductCount <= lastTopUpVisibleCountRef.current
    ) {
      return;
    }

    loadMore(false);
  }, [
    collectionHandle,
    hasNextPage,
    isBelowMd,
    isBelowMg,
    isFetching,
    isLoadingMore,
    loadMore,
    loaded.requestId,
    productDeficit,
    requestId,
    visibleProductCount,
  ]);

  // Parse quick_add_enabled metafield (value is string "true" | "false" from Storefront API)
  const quickAddEnabled = useMemo(() => {
    const value = displayCollection?.quickAddEnabled?.value ?? "";
    return value === "true" || value === "1";
  }, [displayCollection?.quickAddEnabled?.value]);

  // Extract SEO title with fallback: seo.title -> titleOverride -> title
  const seoTitle = useMemo(() => {
    const seoTitleValue = displayCollection?.seo?.title;
    const titleOverrideValue = displayCollection?.titleOverride?.value;
    const titleValue = displayCollection?.title;

    return seoTitleValue ?? titleOverrideValue ?? titleValue ?? null;
  }, [
    displayCollection?.seo?.title,
    displayCollection?.titleOverride?.value,
    displayCollection?.title,
  ]);

  return {
    adjacentCollections: displayCollection?.adjacentCollections
      ? formatAdjacentCollections(
          displayCollection?.adjacentCollections,
          displayCollection?.disableAdjacentCollectionTitleOverride
        )
      : null,
    breadcrumbs: displayCollection?.breadcrumbs?.references.nodes ?? null,
    contentCards,
    description:
      displayCollection?.descriptionOverride?.value ??
      displayCollection?.description ??
      null,
    handle: displayCollection?.handle,
    hasNextPage,
    heroImage: displayCollection?.heroImage?.reference.image,
    isLoading,
    isLoadingMore,
    listReplaced: loaded.replaced,
    loadMore,
    loadingMoreCount: fetchSize,
    pageOnScroll: pageOnScroll ? parseInt(pageOnScroll) : null,
    pageSize,
    pendingTopUp,
    productDeficit,
    productResults: loaded.products,
    productPositions,
    quickAddEnabled,
    seoTitle,
    // Null until the current request has folded, so `hasNextPage` and
    // `pendingTopUp` describe the same page as the products.
    settledRequestId:
      requestId !== null && loaded.requestId === requestId ? requestId : null,
    showContentCards,
    sortOptions: displayCollection?.sortOptions?.references.nodes ?? null,
    topUpStalled,
    title:
      displayCollection?.titleOverride?.value ??
      displayCollection?.title ??
      null,
    variantEligibleProductResults,
    variantMatchFilter,
  };
}
